import { useCallback, useEffect, useState, useMemo } from 'react'
import type { FC } from 'react'
import type {
	EmbedFloorResult,
	FloorResult,
} from '@alexanderathoodly/data-models'
import {
	Map,
	Source,
	Layer,
	NavigationControl,
	LngLatBoundsLike,
	useMap,
} from 'react-map-gl'
import { ChakraProps, Flex, Box, Tooltip, Select } from '@chakra-ui/react'
import { LiftIcn } from '@alexanderathoodly/ui-library'
import { getBounds } from '~/utils/map'

const mapColors = {
	bigOfficeArea: '#87CEFA', // Light Sky Blue
	flexOfficeArea: '#66c2a5',
	lounge: '#ffd92f',
	meetingRoom: '#94d4bf',
	officeRoomSelected: '#007aff', // blue.500
	officeRoomNoMatch: '#CEE6FF', // blue.100
	structure: '#f2efe9',
	walls: '#454545',
}

interface FloorplanFloorControlProps {
	floors: EmbedFloorResult[]
	activeFloor: string | undefined
	selectFloor: (floorId: string) => void
}

const FloorplanFloorControl: FC<FloorplanFloorControlProps & ChakraProps> = ({
	floors,
	activeFloor,
	selectFloor,
	...rest
}) => {
	return (
		<Flex h="fit-content" alignItems="center" {...rest}>
			<LiftIcn h={6} w={6} mr={3} color="blue.500" />
			<Select
				value={activeFloor}
				maxW={20}
				size="sm"
				onChange={(e) => {
					selectFloor(e.currentTarget.value)
				}}
				background="white"
			>
				{floors.map((floor) => (
					<option key={floor.id} value={floor.id}>
						{floor.name}
					</option>
				))}
			</Select>
		</Flex>
	)
}

const LegendItem: FC<{ text: string; color: string } & ChakraProps> = ({
	text,
	color,
	...rest
}) => {
	// isActive to make it work on mobile as well, when clicking a legend color.
	const [isActive, setIsActive] = useState(false)
	return (
		<Tooltip label={text} isOpen={isActive}>
			<Box
				h={4}
				w={4}
				background={color}
				onMouseEnter={() => setIsActive(true)}
				onMouseLeave={() => setIsActive(false)}
				{...rest}
			/>
		</Tooltip>
	)
}

const SolutionFloorplanLegend: FC<ChakraProps> = ({ ...rest }) => {
	return (
		<Flex
			p={2}
			w="fit-content"
			border="1px"
			borderRadius="base"
			borderColor="grey.100"
			background="white"
			shadow="xl"
			gap={2}
			cursor="default"
			{...rest}
		>
			<LegendItem
				text="Vald kontorslösning"
				color={mapColors.officeRoomSelected}
			/>
			<LegendItem text="Kontorsrum" color={mapColors.officeRoomNoMatch} />
			<LegendItem text="Mötesrum" color={mapColors.meetingRoom} />
			<LegendItem
				text="Lounge"
				color={`${mapColors.lounge}26`}
				border={`1.5px solid ${mapColors.lounge}B3`}
			/>
			<LegendItem
				text="Flex-desk"
				color={`${mapColors.flexOfficeArea}26`}
				border={`1.5px solid ${mapColors.flexOfficeArea}B3`}
			/>
		</Flex>
	)
}

interface SolutionFloorplanMapProps {
	floors: FloorResult[] // the floors with bounds that could be shown from solution list.
	solutionFloor: string | undefined // the floor for the selected solution to be displayed.
	activeRooms: string[] | undefined // rooms to be highlighted(rooms in soution)
	height?: number
}

const SolutionFloorplanMap: FC<SolutionFloorplanMapProps> = ({
	floors,
	solutionFloor,
	activeRooms,
	height = 500,
}) => {
	const [viewport, setViewport] = useState({
		latitude: 59.3253,
		longitude: 18.0628,
	})
	const [activeFloorId, setActiveFloorId] = useState<string>()
	const activeFloor = useMemo(() => {
		return floors.find((floor) => floor.id == activeFloorId)
	}, [floors, activeFloorId])
	const roomIds = useMemo(() => {
		return activeRooms || []
	}, [activeRooms])

	useEffect(() => {
		if (floors.length) {
			setActiveFloorId(
				solutionFloor && floors.find((f) => f.id === solutionFloor)
					? solutionFloor
					: floors[0]?.id
			)
		}
	}, [solutionFloor, floors])

	return floors.length > 0 && activeFloor ? (
		<Map
			{...viewport}
			style={{
				width: '100%',
				height,
			}}
			mapStyle="mapbox://styles/mapbox/light-v9"
			mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
			onMove={(event) => setViewport(event.viewState)}
			scrollZoom={false}
		>
			<SolutionAutoZoom floor={activeFloor} roomIds={roomIds} />

			<Flex direction="row" gap={7} alignItems="center" mt={1}>
				<Box
					px={3}
					py={2}
					w="fit-content"
					display={{ lg: 'none' }}
					border="1px"
					borderRadius="base"
					borderColor="grey.100"
					background="white"
					shadow="xl"
					zIndex={2}
				>
					<FloorplanFloorControl
						floors={floors}
						activeFloor={activeFloor.id}
						selectFloor={(floorId: string) => setActiveFloorId(floorId)}
					/>
				</Box>
				<SolutionFloorplanLegend ml={1} zIndex={2} />
				<FloorplanFloorControl
					floors={floors}
					activeFloor={activeFloor.id}
					selectFloor={(floorId: string) => setActiveFloorId(floorId)}
					display={{ base: 'none', lg: 'flex' }}
				/>
			</Flex>
			<NavigationControl
				style={{ bottom: 30, right: 10 }}
				showCompass={false}
			/>
			{activeFloor && (
				<>
					<Source
						id={`tiles-${activeFloor.id}`}
						key={`tiles-${activeFloor.id}`}
						type="vector"
						url={`https://${
							import.meta.env.VITE_YTA_API_DOMAIN
						}/serviced_offices/floors/${activeFloor.id}/tilejson`}
					>
						<Layer
							id={`structure-fill-${activeFloor.id}`}
							type="fill"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							paint={{
								'fill-color': mapColors.structure,
							}}
						/>
						<Layer
							id={`floorplan-meeting-rooms-fill-${activeFloor.id}`}
							type="fill"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={['==', ['get', 'type'], 'meeting_room']}
							paint={{
								'fill-color': mapColors.meetingRoom,
							}}
						></Layer>
						<Layer
							id={`floorplan-office-room-fill-${activeFloor.id}`}
							type="fill"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={['==', ['get', 'type'], 'office_room']}
							paint={{
								'fill-color': [
									'case',
									['in', ['get', 'room_id'], ['literal', roomIds]],
									mapColors.officeRoomSelected,
									mapColors.officeRoomNoMatch,
								],
							}}
						></Layer>
						<Layer
							id={`floorplan-big-office-area-fill-${activeFloor.id}`}
							type="fill"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={['==', ['get', 'type'], 'big_office_area']}
							paint={{
								'fill-color': [
									'case',
									['in', ['get', 'room_id'], ['literal', roomIds]],
									mapColors.officeRoomSelected,
									'transparent',
								],
							}}
						></Layer>
						<Layer
							id={`floorplan-areas-fill-${activeFloor.id}`}
							type="fill"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={[
								'in',
								['get', 'type'],
								['literal', ['flex_office_area', 'lounge']],
							]}
							paint={{
								'fill-opacity': 0.15,
								'fill-color': [
									'case',
									['==', ['get', 'type'], 'flex_office_area'],
									mapColors.flexOfficeArea,
									['==', ['get', 'type'], 'lounge'],
									mapColors.lounge,
									mapColors.structure, // This should never be used, the above match should be exhaustive.
									// But a color was needed to be set for layer to render at all
								],
							}}
						></Layer>
						<Layer
							id={`structure-line-${activeFloor.id}`}
							type="line"
							source-layer="default"
							paint={{ 'line-color': mapColors.walls }}
						></Layer>
						<Layer
							id={`floorplan-areas-line-${activeFloor.id}`}
							type="line"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={[
								'in',
								['get', 'type'],
								['literal', ['flex_office_area', 'lounge']],
							]}
							paint={{
								'line-opacity': 0.7,
								'line-color': [
									'case',
									['==', ['get', 'type'], 'flex_office_area'],
									mapColors.flexOfficeArea,
									['==', ['get', 'type'], 'lounge'],
									mapColors.lounge,
									['==', ['get', 'type'], 'lounge'],
									mapColors.lounge,
									mapColors.structure, // This should never be used, the above match should be exhaustive.
									// But a color was needed to be set for layer to render at all
								],
							}}
						></Layer>
						<Layer
							id={`floorplan-big-office-areas-line-${activeFloor.id}`}
							type="line"
							source={`tiles-${activeFloor.id}`}
							source-layer="default"
							filter={['==', ['get', 'type'], 'big_office_area']}
							paint={{
								'line-color': [
									'case',
									['in', ['get', 'room_id'], ['literal', roomIds]],
									mapColors.officeRoomSelected,
									'transparent',
								],
								'line-opacity': 0.5,
								'line-width': 2,
							}}
						></Layer>
					</Source>
					<Source
						id={`labels-${activeFloor.id}`}
						key={`labels-${activeFloor.id}`}
						type="geojson"
						data={`https://${
							import.meta.env.VITE_YTA_API_DOMAIN
						}/serviced_offices/floors/${activeFloor.id}/room_labels`}
					>
						<Layer
							id={`labels-${activeFloor.id}-layer`}
							type="symbol"
							source={`labels-${activeFloor.id}`}
							filter={['in', ['get', 'room_id'], ['literal', roomIds]]}
							paint={{
								'text-color': 'white',
							}}
							layout={{
								'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
								'text-justify': 'left',
								'text-field': [
									'concat',
									['get', 'external_id'],
									'\n',
									['get', 'size'],
									' m²',
								],
								'text-size': [
									'interpolate',
									['exponential', 2],
									['zoom'],
									18,
									8,
									20,
									10,
									21,
									12,
								],
								'text-anchor': 'center',
							}}
						></Layer>
					</Source>
				</>
			)}
		</Map>
	) : null
}

type AutoZoomProps = {
	floor: FloorResult
	roomIds: string[]
}

function SolutionAutoZoom({ floor, roomIds }: AutoZoomProps) {
	const { current } = useMap()
	const [hasZoomedIn, setHasZoomedIn] = useState(false)

	useEffect(() => {
		if (!current) {
			return
		}
		if (floor && floor.bounds) {
			const newBounds: LngLatBoundsLike = [
				[floor.bounds.west, floor.bounds.south],
				[floor.bounds.east, floor.bounds.north],
			]
			// There seems to be a timing issue when trying to dezoom immediately (nothing happens)
			// the reason is at this point unkown. It could be that we are sending other requests at
			// the same time (ex: changing the source or style) and that it affects mapbox
			// It could also be that we have a competing update somewhere else but we have not found it yet
			setTimeout(() => {
				current.fitBounds(newBounds, { animate: false })
				setHasZoomedIn(false)
			}, 200)
		}
	}, [current, roomIds, floor, setHasZoomedIn])

	const zoomOnRooms = useCallback(() => {
		if (!current || roomIds.length === 0 || hasZoomedIn) {
			return
		}
		const activeFeatures = current.querySourceFeatures(`tiles-${floor.id}`, {
			filter: ['in', ['get', 'room_id'], ['literal', roomIds]],
			sourceLayer: 'default',
		})
		if (!activeFeatures.length) {
			return
		}
		const bounds = getBounds(activeFeatures)
		current.fitBounds(bounds, { zoom: 19 })
		setHasZoomedIn(true)
	}, [current, roomIds, floor, hasZoomedIn])

	useEffect(() => {
		if (!current) {
			return
		}
		current.on('idle', zoomOnRooms)
		return () => {
			current.off('idle', zoomOnRooms)
		}
	}, [zoomOnRooms, current])

	return null
}

export default SolutionFloorplanMap
